import {
  PlanName,
  SubscriptionStatus,
  useDbtConnectionQuery,
} from "@/apollo/types";
import DbtSymbol from "@/assets/dbt/dbt-symbol.svg?react";
import { Button } from "@/components/elements/Button";
import { useCurrentPlan, useSubscriptionStatus } from "@/features/subscription";
import { useDisclosure } from "@/hooks/useDisclosure";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useToast } from "@/providers/ToastProvider";
import { LinkWithSlug } from "@/router";
import { GitBranchIcon, SettingsIcon } from "lucide-react";

import { DbtSetupSlideOver } from "./DbtSetupSlideOver";

export function DbtContent() {
  const toast = useToast();
  const mixpanel = useMixpanel();
  const setupDbtDisclosure = useDisclosure();
  const plan = useCurrentPlan();
  const { status } = useSubscriptionStatus();

  const { data, loading: isLoadingDbtConnection } = useDbtConnectionQuery({
    fetchPolicy: "cache-and-network",
  });
  const dbtConnection = data?.dbtConnection;

  if (isLoadingDbtConnection && !dbtConnection) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading connection details...
      </div>
    );
  }

  if (!dbtConnection) {
    return (
      <>
        <div className="flex items-center justify-between rounded-sm border border-dashed px-4 py-3">
          <span className="text-sm text-muted-foreground">
            No dbt account connected yet
          </span>
          <Button
            onClick={() => {
              mixpanel.track("Transform Settings - Connect Dbt Clicked", {
                step: 2,
              });
              setupDbtDisclosure.onOpen();
            }}
            icon={<DbtSymbol />}
            isLoading={isLoadingDbtConnection}
            loadingText="Checking..."
            size="sm"
          >
            Connect dbt
          </Button>
        </div>
        {(plan.name === PlanName.Basic ||
          status === SubscriptionStatus.Freemium) && (
          <p className="mt-2 text-xs text-muted-foreground">
            <em>
              dbt job triggers are part of Orchestration, included in the
              <strong> Premium plan</strong>.
            </em>
          </p>
        )}
        <DbtSetupSlideOver
          show={setupDbtDisclosure.isOpen}
          onClose={setupDbtDisclosure.onClose}
          onConnected={() => {
            setupDbtDisclosure.onClose();
            toast(
              "dbt successfully connected",
              "You can now set up triggers for dbt jobs via Orchestration",
              "success",
            );
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <GitBranchIcon className="size-3.5" />
          Connection
        </span>
        <span className="font-medium">
          <Button
            variant="link"
            as={LinkWithSlug}
            to={`/settings/connections/${dbtConnection.id}`}
            className="h-auto p-0 font-medium"
          >
            View connection details
          </Button>
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-green-600 dark:text-green-400">
          Connected
        </span>
        <Button
          onClick={setupDbtDisclosure.onOpen}
          variant="outline"
          colorScheme="secondary"
          size="xs"
          icon={<SettingsIcon className="size-3.5" />}
        >
          Configure
        </Button>
      </div>

      {plan.features.orchestration && (
        <p className="mt-2 text-xs text-muted-foreground">
          <em>
            You can now set up automatic triggers for dbt jobs via
            Orchestration.
          </em>
        </p>
      )}

      <DbtSetupSlideOver
        show={setupDbtDisclosure.isOpen}
        onClose={setupDbtDisclosure.onClose}
        onConnected={() => {
          setupDbtDisclosure.onClose();
          toast(
            "dbt successfully connected",
            "You can now set up triggers for dbt jobs via Orchestration",
            "success",
          );
        }}
      />
    </>
  );
}
